/**
 * ContributorPrsPanel — the "Review & deploy" tab inside the Agents page.
 *
 * Only the approver merges to master (BackendAPI) / main (Frontend), so every
 * pull request a contributor opens there is waiting on him to review, merge and
 * therefore deploy. Each row expands into everything needed to decide — what the
 * PR changes, its CI, reviews, Jira ticket and production-impact flags — and the
 * actions to take: approve, request changes, mark ready, merge & deploy.
 * The approver's own PRs are not listed: he merges his own work outside this queue.
 *
 * A PR that names no TRUS ticket gets one automatically (backend sync, every
 * 10 min; "Sync Jira tickets" runs it now).
 *
 * Backend: /api/platform/v1/agents/contributor-prs[...] (WeaveIn super_admin /
 * ops_admin; actions also need agent-control rights).
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
  RefreshCw,
  Loader2,
  GitPullRequest,
  GitMerge,
  Ticket,
  CheckCircle2,
  XCircle,
  Clock,
  CircleDashed,
  HelpCircle,
  ChevronRight,
  ChevronDown,
  AlertTriangle,
  ExternalLink,
  ThumbsUp,
  MessageSquare,
  Rocket,
  Eye,
} from 'lucide-react';
import {
  agentsApi,
  type ContributorPr,
  type ContributorPrDetail,
  type ContributorPrsResponse,
} from '../../shared/services/platformApi';

const CI_BADGE: Record<string, { label: string; cls: string; Icon: React.ElementType }> = {
  success: { label: 'CI passing', cls: 'bg-emerald-50 text-emerald-700', Icon: CheckCircle2 },
  failed: { label: 'CI failing', cls: 'bg-red-50 text-red-700', Icon: XCircle },
  pending: { label: 'CI running', cls: 'bg-amber-50 text-amber-700', Icon: Clock },
  none: { label: 'No CI', cls: 'bg-gray-100 text-gray-500', Icon: CircleDashed },
  unknown: { label: 'CI unknown', cls: 'bg-orange-50 text-orange-700', Icon: HelpCircle },
};

const MERGEABLE: Record<string, { label: string; cls: string }> = {
  clean: { label: 'Mergeable', cls: 'text-emerald-700' },
  unstable: { label: 'Mergeable (non-required checks failing)', cls: 'text-amber-700' },
  behind: { label: 'Behind base branch: author should merge the latest base', cls: 'text-amber-700' },
  dirty: { label: 'Merge conflicts: author must resolve', cls: 'text-red-700' },
  blocked: { label: 'Blocked by branch protection or required checks', cls: 'text-red-700' },
  draft: { label: 'Draft: mark ready to merge', cls: 'text-gray-600' },
  unknown: { label: 'GitHub is still computing mergeability', cls: 'text-gray-500' },
};

// ---------------------------------------------------------------- page cache
//
// The Agents page remounts while auth inputs settle, and this tab unmounts when
// another tab is selected; each mount used to refetch the list (6-13 s) and every
// re-opened row its detail (3-5 s), blanking the panel each time. Results now
// live at module scope (per browser tab): shown instantly, revalidated quietly in
// the background when older than FRESH_MS, and never fetched twice at once.

const FRESH_MS = 30_000;
let listStore: { data: ContributorPrsResponse; at: number } | null = null;
const detailStore = new Map<string, { data: ContributorPrDetail; at: number }>();
const inflight = new Map<string, Promise<unknown>>();
// Which rows are open survives remounts too.
const openRows = new Set<string>();

function dedupe<T>(key: string, fn: () => Promise<T>): Promise<T> {
  const running = inflight.get(key) as Promise<T> | undefined;
  if (running) return running;
  const p = fn().finally(() => inflight.delete(key));
  inflight.set(key, p);
  return p;
}

const isFresh = (at?: number) => !!at && Date.now() - at < FRESH_MS;

function age(iso?: string | null): string {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  const h = Math.floor(ms / 3_600_000);
  if (h < 1) return 'just now';
  if (h < 48) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function RepoBadge({ pr }: { pr: ContributorPr }) {
  const be = pr.repo_short === 'be';
  return (
    <span
      className={`text-[11px] px-1.5 py-0.5 rounded font-semibold ${be ? 'bg-indigo-50 text-indigo-700' : 'bg-teal-50 text-teal-700'}`}
      title={pr.repo}
    >
      {be ? 'Backend' : 'Frontend'}
    </span>
  );
}

function CiBadge({ state, title }: { state?: string; title?: string }) {
  const ci = CI_BADGE[state || 'none'] || CI_BADGE.none;
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full ${ci.cls}`} title={title}>
      <ci.Icon className="w-3 h-3" />
      {ci.label}
    </span>
  );
}

function JiraLink({ pr, base }: { pr: ContributorPr; base: string }) {
  if (!pr.jira_key) {
    return <span className="text-xs text-amber-700" title="Created by the next sync">No ticket yet</span>;
  }
  return (
    <a
      href={`${base}/${pr.jira_key}`}
      target="_blank"
      rel="noreferrer"
      onClick={(e) => e.stopPropagation()}
      className="inline-flex items-center gap-1 text-xs text-emerald-700 hover:underline"
    >
      <Ticket className="w-3 h-3" />
      {pr.jira_key}
    </a>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wide text-gray-400 mb-1">{title}</div>
      {children}
    </div>
  );
}

const btn =
  'inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md border disabled:opacity-50 disabled:cursor-not-allowed';

// ---------------------------------------------------------------- detail view

function PrDetail({
  pr,
  base,
  canControl,
  onChanged,
}: {
  pr: ContributorPr;
  base: string;
  canControl: boolean;
  onChanged: () => void;
}) {
  const storeKey = `${pr.repo_short}:${pr.number}`;
  const [d, setD] = useState<ContributorPrDetail | null>(() => detailStore.get(storeKey)?.data ?? null);
  const [loading, setLoading] = useState(() => !detailStore.has(storeKey));
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [comment, setComment] = useState('');
  const [force, setForce] = useState(false);
  const [showAllBody, setShowAllBody] = useState(false);

  // force: skip the client freshness check and the API's 30 s cache (after an action).
  const load = useCallback(
    async (force = false) => {
      const cachedEntry = detailStore.get(storeKey);
      if (!force && isFresh(cachedEntry?.at)) return;
      setLoading(true);
      setErr(null);
      const res = await dedupe(`detail:${storeKey}:${force}`, () =>
        agentsApi.getContributorPr(pr.repo_short, pr.number, force),
      );
      if (res.success && res.data) {
        detailStore.set(storeKey, { data: res.data, at: Date.now() });
        setD(res.data);
      } else if (!cachedEntry) {
        setErr(res.error?.message || 'Could not load this PR.');
      }
      setLoading(false);
    },
    [storeKey, pr.repo_short, pr.number],
  );

  useEffect(() => {
    load();
  }, [load]);

  const run = async (
    key: string,
    call: () => Promise<{ success: boolean; data?: any; error?: { message: string } }>,
    okText: (data: any) => string,
  ) => {
    setBusy(key);
    setMsg(null);
    const res = await call();
    const data = res.data || {};
    if (res.success && data.ok !== false && data.merged !== false) {
      const jira = data.jira_key
        ? data.jira_logged
          ? ` Logged on ${data.jira_key}${data.jira_created ? ' (ticket created now)' : ''}.`
          : ` Jira not updated${data.jira_error ? `: ${data.jira_error}` : ''}.`
        : data.jira_error
          ? ` Jira not updated: ${data.jira_error}.`
          : '';
      setMsg({ ok: true, text: okText(data) + jira });
      setComment('');
      await load(true);
      onChanged();
    } else {
      setMsg({ ok: false, text: data.reason || res.error?.message || 'GitHub refused the action.' });
    }
    setBusy(null);
  };

  if (loading && !d) {
    return (
      <div className="flex items-center gap-2 text-xs text-gray-500 py-2">
        <Loader2 className="w-3 h-3 animate-spin" /> Loading PR details…
      </div>
    );
  }
  if (err || !d) return <div className="text-xs text-red-700 py-2">{err || 'Could not load this PR.'}</div>;
  const updating = loading; // a cached detail is on screen; this is a background refresh

  const mergeState = d.draft ? 'draft' : d.mergeable_state || 'unknown';
  const mergeInfo = MERGEABLE[mergeState] || { label: mergeState, cls: 'text-gray-600' };
  const ciBlocks = ['failed', 'pending', 'unknown'].includes(d.ci.state);
  const body = d.body || '';
  const longBody = body.length > 1200;

  const confirmMerge = () => {
    const lines = [
      `Merge ${d.repo} #${d.number} into ${d.base}?`,
      '',
      'This deploys it to PRODUCTION immediately.',
    ];
    if (d.risk_flags.length) {
      lines.push('', 'Production impact:');
      d.risk_flags.forEach((f) => lines.push(`  • ${f.message}`));
    }
    if (force && ciBlocks) lines.push('', `Overriding CI (${d.ci.state}).`);
    if (d.jira?.key) lines.push('', `${d.jira.key} will be moved to Done.`);
    return window.confirm(lines.join('\n'));
  };

  return (
    <div className="space-y-4 pt-2 pb-1">
      {/* Summary strip */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-700">
        <span>
          <span className="text-emerald-700 font-semibold">+{d.additions}</span>{' '}
          <span className="text-red-700 font-semibold">−{d.deletions}</span>
        </span>
        <span>{d.changed_files} file{d.changed_files === 1 ? '' : 's'}</span>
        <span>{d.commit_count} commit{d.commit_count === 1 ? '' : 's'}</span>
        <span className={mergeInfo.cls}>{mergeInfo.label}</span>
        <CiBadge state={d.ci.state} title={d.ci.error || undefined} />
        {updating && (
          <span className="inline-flex items-center gap-1 text-gray-400">
            <Loader2 className="w-3 h-3 animate-spin" /> updating
          </span>
        )}
      </div>

      {/* Production impact */}
      {d.risk_flags.length > 0 && (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 space-y-1">
          <div className="flex items-center gap-1.5 text-xs font-semibold text-amber-900">
            <AlertTriangle className="w-3.5 h-3.5" /> Production impact on merge
          </div>
          {d.risk_flags.map((f) => (
            <div key={f.message} className="text-xs text-amber-900">
              {f.message}
              <span className="text-amber-700 font-mono"> ({f.files.join(', ')})</span>
            </div>
          ))}
        </div>
      )}

      {/* Actions */}
      <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-3 space-y-2">
        {!canControl && (
          <div className="text-xs text-gray-600">Actions need agent-control rights (super_admin / ops_admin).</div>
        )}
        <textarea
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="Review comment for the author (required to request changes)…"
          rows={2}
          disabled={!canControl || !!busy}
          className="w-full text-xs rounded-md border border-gray-300 bg-white px-2 py-1.5"
        />
        <div className="flex flex-wrap items-center gap-2">
          <button
            className={`${btn} border-emerald-300 bg-white text-emerald-800 hover:bg-emerald-50`}
            disabled={!canControl || !!busy}
            onClick={() =>
              run(
                'approve',
                () => agentsApi.reviewContributorPr(d.repo_short, d.number, 'APPROVE', comment),
                () => 'Approved on GitHub.',
              )
            }
          >
            {busy === 'approve' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ThumbsUp className="w-3.5 h-3.5" />}
            Approve
          </button>
          <button
            className={`${btn} border-amber-300 bg-white text-amber-800 hover:bg-amber-50`}
            disabled={!canControl || !!busy || !comment.trim()}
            title={!comment.trim() ? 'Write a comment first' : undefined}
            onClick={() =>
              run(
                'changes',
                () => agentsApi.reviewContributorPr(d.repo_short, d.number, 'REQUEST_CHANGES', comment),
                () => 'Changes requested; the author is notified on GitHub.',
              )
            }
          >
            {busy === 'changes' ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <MessageSquare className="w-3.5 h-3.5" />
            )}
            Request changes
          </button>
          {d.draft && (
            <button
              className={`${btn} border-gray-300 bg-white text-gray-800 hover:bg-gray-100`}
              disabled={!canControl || !!busy}
              onClick={() =>
                run('ready', () => agentsApi.readyContributorPr(d.repo_short, d.number), () => 'Marked ready for review.')
              }
            >
              {busy === 'ready' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Eye className="w-3.5 h-3.5" />}
              Mark ready for review
            </button>
          )}
          <div className="ml-auto flex items-center gap-3">
            {ciBlocks && (
              <label className="inline-flex items-center gap-1 text-xs text-gray-700" title="Merge even though CI is not green">
                <input type="checkbox" checked={force} onChange={(e) => setForce(e.target.checked)} disabled={!canControl} />
                Override CI ({d.ci.state})
              </label>
            )}
            <button
              className={`${btn} border-purple-600 bg-purple-600 text-white hover:bg-purple-700`}
              disabled={!canControl || !!busy || (ciBlocks && !force) || mergeState === 'dirty'}
              title={
                mergeState === 'dirty'
                  ? 'Resolve merge conflicts first'
                  : ciBlocks && !force
                    ? `CI is ${d.ci.state}: wait, or tick Override CI`
                    : 'Squash-merge into the deploy branch: deploys to production'
              }
              onClick={() => {
                if (!confirmMerge()) return;
                run(
                  'merge',
                  () => agentsApi.mergeContributorPr(d.repo_short, d.number, force),
                  (data) =>
                    `Merged, deploying to production.${data.jira_key ? ` ${data.jira_key} ${data.jira_done ? 'moved to Done' : 'could not be moved to Done; update it in Jira'}.` : ''}`,
                );
              }}
            >
              {busy === 'merge' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Rocket className="w-3.5 h-3.5" />}
              Merge &amp; deploy
            </button>
          </div>
        </div>
        {msg && (
          <div className={`text-xs rounded-md px-2 py-1.5 ${msg.ok ? 'bg-emerald-50 text-emerald-800' : 'bg-red-50 text-red-800'}`}>
            {msg.text}
          </div>
        )}
        <div className="flex flex-wrap gap-3 text-xs">
          <a href={d.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-gray-700 hover:underline">
            <ExternalLink className="w-3 h-3" /> Open on GitHub (full diff)
          </a>
          {d.jira_key && (
            <a
              href={`${base}/${d.jira_key}`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-gray-700 hover:underline"
            >
              <ExternalLink className="w-3 h-3" /> Open {d.jira_key} in Jira
            </a>
          )}
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <Section title="Jira ticket">
          {d.jira ? (
            d.jira.error ? (
              <div className="text-xs text-red-700">{d.jira.key}: could not load ({d.jira.error})</div>
            ) : (
              <div className="text-xs text-gray-800 space-y-0.5">
                <div>
                  <span className="font-semibold">{d.jira.key}</span> · {d.jira.summary}
                  <div className="text-gray-400">Every review action here is logged on this ticket.</div>
                </div>
                <div className="text-gray-500">
                  {d.jira.status || 'no status'} · {d.jira.assignee ? `assigned to ${d.jira.assignee}` : 'unassigned'}
                </div>
              </div>
            )
          ) : (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs text-amber-700">No ticket yet.</span>
              <button
                className={`${btn} border-emerald-300 bg-white text-emerald-800 hover:bg-emerald-50`}
                disabled={!canControl || !!busy}
                onClick={() =>
                  run(
                    'ticket',
                    () => agentsApi.ticketContributorPr(d.repo_short, d.number),
                    (data) => (data.jira_key ? `Jira ticket ${data.jira_key} is assigned for review.` : 'Done.'),
                  )
                }
              >
                {busy === 'ticket' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Ticket className="w-3.5 h-3.5" />}
                Create Jira ticket now
              </button>
            </div>
          )}
        </Section>

        <Section title="Reviews">
          {d.reviews.length === 0 ? (
            <div className="text-xs text-gray-500">No reviews yet.</div>
          ) : (
            <div className="space-y-1">
              {d.reviews.map((r, i) => (
                <div key={i} className="text-xs text-gray-800">
                  <span className="font-semibold">{r.user}</span>{' '}
                  <span
                    className={
                      r.state === 'APPROVED'
                        ? 'text-emerald-700'
                        : r.state === 'CHANGES_REQUESTED'
                          ? 'text-amber-700'
                          : 'text-gray-500'
                    }
                  >
                    {r.state.replace('_', ' ').toLowerCase()}
                  </span>{' '}
                  <span className="text-gray-400">{age(r.submitted_at)}</span>
                  {r.body && <div className="text-gray-600 whitespace-pre-wrap">{r.body}</div>}
                </div>
              ))}
            </div>
          )}
        </Section>
      </div>

      <Section title="Description">
        {body.trim() ? (
          <>
            <pre className="text-xs text-gray-800 whitespace-pre-wrap font-sans bg-white border border-gray-200 rounded-md px-3 py-2">
              {longBody && !showAllBody ? `${body.slice(0, 1200)}…` : body}
            </pre>
            {longBody && (
              <button className="text-xs text-gray-600 hover:underline mt-1" onClick={() => setShowAllBody((v) => !v)}>
                {showAllBody ? 'Show less' : 'Show full description'}
              </button>
            )}
          </>
        ) : (
          <div className="text-xs text-amber-700">No description. Ask the author what this changes and why.</div>
        )}
      </Section>

      <div className="grid md:grid-cols-2 gap-4">
        <Section title={`Files changed (${d.changed_files})`}>
          <div className="space-y-0.5 max-h-56 overflow-auto">
            {d.files.map((f) => (
              <div key={f.filename} className="flex items-center gap-2 text-xs">
                <span className="font-mono text-gray-800 truncate flex-1" title={f.filename}>
                  {f.filename}
                </span>
                <span className="text-gray-400">{f.status}</span>
                <span className="text-emerald-700">+{f.additions}</span>
                <span className="text-red-700">−{f.deletions}</span>
              </div>
            ))}
            {d.files_truncated && <div className="text-xs text-gray-500">…more on GitHub</div>}
          </div>
        </Section>

        <Section title={`Commits (${d.commit_count})`}>
          <div className="space-y-0.5 max-h-56 overflow-auto">
            {d.commits.map((c) => (
              <div key={c.sha} className="text-xs text-gray-800">
                <span className="font-mono text-gray-400 mr-1">{c.sha}</span>
                {c.message}
                <span className="text-gray-400">
                  {' '}
                  · {c.author} {age(c.date)}
                </span>
              </div>
            ))}
          </div>
        </Section>
      </div>

      <Section title="CI checks">
        {d.ci.error && <div className="text-xs text-orange-700 mb-1">{d.ci.error}</div>}
        {d.ci.runs.length === 0 ? (
          <div className="text-xs text-gray-500">{d.ci.error ? 'Checks could not be read.' : 'No checks ran on this PR.'}</div>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {d.ci.runs.map((r, i) => {
              const state =
                r.status !== 'completed'
                  ? 'pending'
                  : r.conclusion === 'success'
                    ? 'success'
                    : ['skipped', 'neutral'].includes(r.conclusion || '')
                      ? 'none'
                      : 'failed';
              const badge = (
                <span
                  className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full ${CI_BADGE[state].cls}`}
                >
                  {React.createElement(CI_BADGE[state].Icon, { className: 'w-3 h-3' })}
                  {r.name}
                  {state === 'none' ? ' (skipped)' : ''}
                </span>
              );
              return r.url ? (
                <a key={`${r.name}-${i}`} href={r.url} target="_blank" rel="noreferrer" className="hover:opacity-80">
                  {badge}
                </a>
              ) : (
                <span key={`${r.name}-${i}`}>{badge}</span>
              );
            })}
          </div>
        )}
      </Section>
    </div>
  );
}

// ---------------------------------------------------------------------- panel

export default function ContributorPrsPanel({ canControl = true }: { canControl?: boolean }) {
  const [data, setData] = useState<ContributorPrsResponse | null>(() => listStore?.data ?? null);
  const [loading, setLoading] = useState(() => !listStore);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [open, setOpen] = useState<Record<string, boolean>>(() =>
    Object.fromEntries([...openRows].map((k) => [k, true])),
  );
  const toggle = useCallback((k: string) => {
    setOpen((p) => {
      const next = !p[k];
      if (next) openRows.add(k);
      else openRows.delete(k);
      return { ...p, [k]: next };
    });
  }, []);

  // force: bypass the client freshness check and the API cache (Refresh, after actions).
  const load = useCallback(async (force = false) => {
    if (!force && isFresh(listStore?.at)) return;
    setLoading(true);
    setError(null);
    const res = await dedupe(`list:${force}`, () => agentsApi.getContributorPrs(force));
    if (res.success && res.data) {
      listStore = { data: res.data, at: Date.now() };
      setData(res.data);
    } else {
      setError(res.error?.message || 'Could not load contributor PRs.');
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const reloadAfterAction = useCallback(() => load(true), [load]);

  const sync = useCallback(async () => {
    setSyncing(true);
    setNotice(null);
    setError(null);
    const res = await agentsApi.syncContributorPrs();
    if (res.success && res.data) {
      const c = res.data.counts || {};
      const listErrors = (res.data as { errors?: string[] }).errors || [];
      const created = c.created || 0;
      const failed = c.error || 0;
      setNotice(
        created || failed
          ? `Created ${created} Jira ticket${created === 1 ? '' : 's'}${failed ? `; ${failed} failed (see logs)` : ''}.`
          : 'Every open contributor PR already has a Jira ticket.',
      );
      if (listErrors.length) setError(`Could not read: ${listErrors.join('; ')}`);
      await load(true);
    } else {
      setError(res.error?.message || 'Could not sync Jira tickets.');
    }
    setSyncing(false);
  }, [load]);

  const openPrs = data?.open || [];
  const merged = data?.merged || [];
  const base = data?.jira_browse_base || 'https://psyssurance.atlassian.net/browse';
  const keyOf = (pr: ContributorPr) => `${pr.repo}#${pr.number}`;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs px-2.5 py-1 rounded-full bg-orange-50 text-orange-700">
          Awaiting review: <strong>{openPrs.length}</strong>
        </span>
        <span className="text-xs px-2.5 py-1 rounded-full bg-gray-100 text-gray-600">
          Ready (not draft): <strong>{openPrs.filter((p) => !p.draft).length}</strong>
        </span>
        <span className="text-xs px-2.5 py-1 rounded-full bg-gray-100 text-gray-600">
          Merged, {data?.merged_window_days ?? 7}d: <strong>{merged.length}</strong>
        </span>
        <div className="ml-auto flex items-center gap-2">
          <button
            onClick={sync}
            disabled={syncing || loading}
            className={`${btn} border-gray-300 bg-white hover:bg-gray-50`}
            title="Create a TRUS ticket for every open contributor PR that names none (also runs every 10 minutes)"
          >
            {syncing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Ticket className="w-3.5 h-3.5" />}
            Sync Jira tickets
          </button>
          <button onClick={() => load(true)} disabled={loading} className={`${btn} border-gray-300 bg-white hover:bg-gray-50`}>
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {error && <div className="text-xs text-red-700 bg-red-50 rounded-md px-3 py-2">{error}</div>}
      {notice && <div className="text-xs text-emerald-800 bg-emerald-50 rounded-md px-3 py-2">{notice}</div>}
      {data?.errors && data.errors.length > 0 && (
        <div className="text-xs text-red-800 bg-red-50 rounded-md px-3 py-2 space-y-0.5">
          <div className="font-semibold">Some repositories could not be read, so this list may be incomplete:</div>
          {data.errors.map((e) => (
            <div key={e} className="font-mono">
              {e}
            </div>
          ))}
        </div>
      )}
      {data && !data.configured && (
        <div className="text-xs text-amber-800 bg-amber-50 rounded-md px-3 py-2">
          GitHub isn't configured on this service (GITHUB_TOKEN), so PRs can't be listed.
        </div>
      )}

      <section>
        <h3 className="flex items-center gap-1.5 text-sm font-semibold text-gray-800 mb-2">
          <GitPullRequest className="w-4 h-4 text-orange-600" />
          Awaiting your review &amp; merge
          <span className="text-xs font-normal text-gray-500">Click a PR for details and actions</span>
        </h3>
        {loading && !data ? (
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <Loader2 className="w-3 h-3 animate-spin" /> Loading…
          </div>
        ) : openPrs.length === 0 ? (
          <div className="text-xs text-gray-500">No contributor PRs are waiting on you.</div>
        ) : (
          <div className="divide-y divide-gray-100 border border-gray-200 rounded-md">
            {openPrs.map((pr) => {
              const k = keyOf(pr);
              const isOpen = !!open[k];
              return (
                <div key={k} className="px-3 py-2">
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => toggle(k)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        toggle(k);
                      }
                    }}
                    className="flex flex-wrap items-center gap-x-3 gap-y-1 cursor-pointer"
                  >
                    {isOpen ? (
                      <ChevronDown className="w-4 h-4 text-gray-400" />
                    ) : (
                      <ChevronRight className="w-4 h-4 text-gray-400" />
                    )}
                    <RepoBadge pr={pr} />
                    <div className="flex-1 min-w-[16rem]">
                      <div className="text-sm text-gray-900 break-words">
                        <span className="text-gray-400 font-mono mr-1">#{pr.number}</span>
                        {pr.title}
                      </div>
                      <div className="text-[11px] text-gray-500">
                        {pr.author} · <span className="font-mono">{pr.branch}</span> · opened {age(pr.created_at)}
                      </div>
                    </div>
                    <span
                      className={`text-[11px] px-2 py-0.5 rounded-full ${pr.draft ? 'bg-gray-100 text-gray-600' : 'bg-blue-50 text-blue-700'}`}
                    >
                      {pr.draft ? 'Draft' : 'Ready for review'}
                    </span>
                    <CiBadge
                      state={pr.ci_state}
                      title={pr.ci_error || (pr.ci_failing?.length ? `Failing: ${pr.ci_failing.join(', ')}` : undefined)}
                    />
                    <JiraLink pr={pr} base={base} />
                  </div>
                  {isOpen && (
                    <div className="ml-6 mt-2 border-t border-gray-100">
                      <PrDetail pr={pr} base={base} canControl={canControl} onChanged={reloadAfterAction} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>

      <section>
        <h3 className="flex items-center gap-1.5 text-sm font-semibold text-gray-800 mb-2">
          <GitMerge className="w-4 h-4 text-purple-600" />
          Merged in the last {data?.merged_window_days ?? 7} days
        </h3>
        {merged.length === 0 ? (
          <div className="text-xs text-gray-500">{loading && !data ? '' : 'No contributor PRs merged recently.'}</div>
        ) : (
          <div className="divide-y divide-gray-100 border border-gray-200 rounded-md">
            {merged.map((pr) => (
              <div key={keyOf(pr)} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2">
                <RepoBadge pr={pr} />
                <div className="flex-1 min-w-[16rem]">
                  <a href={pr.url} target="_blank" rel="noreferrer" className="text-sm text-gray-900 hover:underline break-words">
                    <span className="text-gray-400 font-mono mr-1">#{pr.number}</span>
                    {pr.title}
                  </a>
                  <div className="text-[11px] text-gray-500">
                    {pr.author} · merged {age(pr.merged_at)}
                  </div>
                </div>
                <span
                  className={`text-[11px] px-2 py-0.5 rounded-full ${
                    pr.live === true
                      ? 'bg-emerald-50 text-emerald-700'
                      : pr.live === false
                        ? 'bg-amber-50 text-amber-700'
                        : 'bg-gray-100 text-gray-500'
                  }`}
                  title={pr.live == null ? 'Deploy status unavailable (Render/Vercel not configured on this service)' : undefined}
                >
                  {pr.live === true ? 'Live in production' : pr.live === false ? 'Deploying' : 'Deploy status unknown'}
                </span>
                <JiraLink pr={pr} base={base} />
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
